import { Icon } from '@progress/kendo-react-common';
import {
    InputClearValue,
    InputSeparator,
    InputSuffix,
    NumericTextBox,
    NumericTextBoxChangeEvent,
    TextBox,
    TextBoxChangeEvent,
    TextBoxProps
} from '@progress/kendo-react-inputs'
import { Error, Label } from '@progress/kendo-react-labels';
import { useState, useCallback, useEffect } from 'react';

interface ITextField {
    onChange: (value: any) => void;
    title?: any;
    value?: string | number;
    placeHolder?: string;
    hasSuffix?: boolean;
    submitButton?: any;
    size?: 'small' | 'medium' | 'large';
    fullwidth?: boolean;
    required?: boolean;
    numeric?: boolean;
    type?: 'email' | 'password' | 'text';
    readOnly?: boolean;
    prefix?: any;
}

/**
 *
 * @param onChange (value:string) => void - returns changed value to parent
 * @param title string? - label printed above the text box
 * @param value string? - used for defaulting the contents in the text box
 * @param placeHolder string? - placeholder text to display when textbox is empty
 * @param hasSuffix boolean? - hides or shows suffix components clear text box and custom button
 * @param submitButton React.Component - component to display in suffix of text box (Used for single input forms)
 * @param size string? - "small" | "medium" | "large" sets width of the text box
 * @param fullwidth boolean? - overrides size to take up as much space as possible
 * @param numeric boolean? - sets the component to only accept numeric values and adds +/- incrementing
 * @param type string? - "email" | "password" | "text" adjusts the textbox input validation and display
 * @param required boolean? - shows required message if textbox is empty
 * @returns
 */
const TextField = (props: ITextField) => {
    const { onChange, title, value, placeHolder, hasSuffix, submitButton, size, fullwidth, numeric, type, readOnly, required } = props;
    const [textValue, setTextValue] = useState<TextBoxProps['value']>(value ?? '')
    const [numValue, setNumValue] = useState<number | null>(value ? +value : 0);
    const [style] = useState<string>(fullwidth ? '100%' : size === 'large' ? '60%' : size === 'medium' ? '40%' : '30%')

    const handleChange = useCallback((event: TextBoxChangeEvent) => {
        onChange(event.target.value);
        setTextValue(event.target.value);
    }, [onChange]);

    const handleNumericChange = useCallback((event: NumericTextBoxChangeEvent) => {
        onChange(event.target.value);
        setNumValue(event.target.value);
    }, [onChange])

    const handleClear = useCallback(() => {
        setTextValue('');
        onChange('');
    }, [onChange])

    useEffect(() => {
        setTextValue(value ?? '');
        if (numeric) {
            setNumValue(value ? +value : 0)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value])

    const renderTextBoxWithSuffix = () => {
        return (
            <>
                {title ? (
                    <div>
                        <Label editorId={title}>{title}</Label>
                    </div>
                ) : null}
                <TextBox
                    type={type ?? ''}
                    value={textValue ?? ''}
                    placeholder={placeHolder ?? ''}
                    onChange={handleChange}
                    suffix={() => (
                        <>
                            {textValue !== '' && (
                                <InputClearValue onClick={handleClear}>
                                    <Icon name='x' />
                                </InputClearValue>
                            )}
                            {submitButton ? (
                                <>
                                    <InputSeparator />
                                    <InputSuffix>
                                        {submitButton}
                                    </InputSuffix>
                                </>
                            ) : null}
                        </>

                    )}
                    prefix={() => (props.prefix ?? '')}
                    style={{ width: style }}
                    readOnly={readOnly ?? false}
                    required={props.required}
                />
                {/* {(!textValue && required) && <Error id={props.title ?? 'unknown'}>This field is required</Error>} */}
            </>
        )
    }

    const renderTextBoxWithoutSuffix = () => {
        return (
            <>
                <div style={{display:'flex', flexDirection:'row'}}>
                    {title ? (
                        <div style={{marginRight:'10px'}}>
                            <Label editorId={title}>{title}</Label>
                        </div>
                    ) : null}
                    {(!textValue && required) && <Error id={props.title ?? 'unknown'}>This field is required</Error>}
                </div>
                <TextBox
                    type={type ?? ''}
                    value={textValue ?? ''}
                    placeholder={placeHolder ?? ''}
                    onChange={handleChange}
                    style={{ width: style }}
                    readOnly={readOnly ?? false}
                    required={required}
                />
            </>
        )
    }

    const renderNumericTextBox = () => {
        return (
            <>
                <div style={{display:'flex', flexDirection:'row'}}>
                    {title ? (
                        <div style={{marginRight:'10px'}}>
                            <Label editorId={title}>{title}</Label>
                        </div>
                    ) : null}
                    {(!textValue && required) && <Error id={props.title ?? 'unknown'}>This field is required</Error>}
                </div>
                <NumericTextBox
                    value={numValue ?? 0}
                    onChange={handleNumericChange}
                    style={{ width: style }}
                    readOnly={readOnly ?? false}
                    required={required}
                />
            </>
        )
    }

    return (
        <>
            {numeric ?
                renderNumericTextBox()
                :
                hasSuffix ?
                    renderTextBoxWithSuffix()
                    :
                    renderTextBoxWithoutSuffix()
            }
        </>
    )
}

export default TextField